import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck,
  X,
  Tag,
  Prohibit,
  CheckCircle,
  SpinnerGap,
  FloppyDisk,
} from '@phosphor-icons/react';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  primary:    { label: 'Primary',    help: 'Personal + important' },
  updates:    { label: 'Updates',    help: 'Bills, receipts, confirmations' },
  promotions: { label: 'Promotions', help: 'Marketing & offers (noisy)' },
  social:     { label: 'Social',     help: 'Facebook, LinkedIn, etc.' },
  forums:     { label: 'Forums',     help: 'Mailing lists' },
};

/**
 * Reusable multi-value chip-input. Accepts entries separated by Enter or comma.
 * Keeps itself controlled via value / onChange.
 */
function ChipInput({ value = [], onChange, placeholder, testIdPrefix, validate }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const commit = () => {
    const raw = draft.trim().replace(/,$/, '');
    if (!raw) return;
    const ok = validate ? validate(raw) : true;
    if (ok !== true) {
      setError(ok || 'Invalid value');
      return;
    }
    if (value.includes(raw)) {
      setDraft('');
      return;
    }
    onChange([...value, raw]);
    setDraft('');
    setError('');
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 p-2 border border-[#ebeef2] rounded-xl bg-white focus-within:border-[#005bc1] focus-within:ring-2 focus-within:ring-[#d6e3ff] transition-all">
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 bg-[#eef4ff] text-[#004faa] text-xs font-medium px-2 py-1 rounded-lg"
            data-testid={`${testIdPrefix}-chip-${i}`}
          >
            {v}
            <button
              type="button"
              onClick={() => remove(i)}
              className="hover:text-[#8a0707] transition-colors"
              aria-label={`Remove ${v}`}
              data-testid={`${testIdPrefix}-chip-remove-${i}`}
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(''); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
            if (e.key === 'Backspace' && !draft && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] bg-transparent text-sm outline-none"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      {error && (
        <p className="text-xs text-[#a61919] mt-1 flex items-center gap-1">
          <X size={10} /> {error}
        </p>
      )}
    </div>
  );
}

/**
 * GmailFiltersPanel
 * -----------------
 * Self-contained UI for Gmail privacy filters. Fetches + saves its own state.
 * Drop anywhere inside <Connections/>. Props optional.
 */
export default function GmailFiltersPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [prefs, setPrefs] = useState({
    sender_blocklist: [],
    label_blocklist: [],
    category_policy: {
      primary: true, updates: true, forums: true, promotions: false, social: false,
    },
    subject_blocklist: [],
  });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/gmail/filters`, { withCredentials: true });
        if (!alive) return;
        setPrefs((p) => ({ ...p, ...(data?.prefs || {}) }));
      } catch (err) {
        console.error('Load gmail filters failed:', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/gmail/filters`,
        prefs,
        { withCredentials: true }
      );
      setPrefs((p) => ({ ...p, ...(data?.prefs || {}) }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('Save filters failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (key) => {
    setPrefs((p) => ({
      ...p,
      category_policy: { ...p.category_policy, [key]: !p.category_policy?.[key] },
    }));
  };

  const validateSender = (raw) => {
    const s = raw.trim().toLowerCase();
    if (!s) return 'Required';
    if (/\s/.test(s)) return 'No spaces allowed';
    // Accept: a@b.com | @b.com | *@b.com | b.com
    if (/^\*?@?[a-z0-9.\-_]+\.[a-z]{2,}$/.test(s)) return true;
    if (/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/.test(s)) return true;
    return 'Use: a@b.com, @domain.com, or domain.com';
  };

  const enabledCategoriesCount = useMemo(
    () => Object.values(prefs.category_policy || {}).filter(Boolean).length,
    [prefs.category_policy]
  );

  const activeFilterCount = useMemo(() => {
    const senders = (prefs.sender_blocklist || []).length;
    const labels = (prefs.label_blocklist || []).length;
    const skippedCats = Object.values(prefs.category_policy || {}).filter((v) => !v).length;
    return { senders, labels, skippedCats, total: senders + labels + skippedCats };
  }, [prefs.sender_blocklist, prefs.label_blocklist, prefs.category_policy]);

  if (loading) {
    return (
      <div className="surface-card p-6 flex items-center gap-2 text-sm text-[#596065]">
        <SpinnerGap className="animate-spin" size={16} /> Loading filters…
      </div>
    );
  }

  return (
    <div className="surface-card p-0 overflow-hidden" data-testid="gmail-filters-panel">
      {/* Header */}
      <div className="p-5 border-b border-[#ebeef2] bg-gradient-to-br from-[#f8fafc] to-white">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#d6e3ff] flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={18} className="text-[#004faa]" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-[#2d3338]">Privacy & Filters</h3>
            <p className="text-xs text-[#596065] mt-0.5">
              Tell LifeAdmin which emails to skip. Filtered messages are never fetched
              from Gmail — they never reach our servers.
            </p>
            {activeFilterCount.total > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0f6b3a] bg-[#ecfdf5] border border-[#bbf7d0] rounded-full px-2.5 py-1" data-testid="filters-active-summary">
                <CheckCircle size={12} weight="fill" /> Active on next scan:
                {activeFilterCount.senders > 0 && <span>{activeFilterCount.senders} sender{activeFilterCount.senders !== 1 ? 's' : ''}</span>}
                {activeFilterCount.senders > 0 && (activeFilterCount.labels > 0 || activeFilterCount.skippedCats > 0) && <span>·</span>}
                {activeFilterCount.labels > 0 && <span>{activeFilterCount.labels} label{activeFilterCount.labels !== 1 ? 's' : ''}</span>}
                {activeFilterCount.labels > 0 && activeFilterCount.skippedCats > 0 && <span>·</span>}
                {activeFilterCount.skippedCats > 0 && <span>{activeFilterCount.skippedCats} categor{activeFilterCount.skippedCats !== 1 ? 'ies' : 'y'}</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Two-column grid on desktop; each "section" is a cell. On mobile they
          stack vertically with horizontal dividers. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">

      {/* A — Sender blocklist */}
      <section className="p-5 border-b lg:border-r border-[#ebeef2]" data-testid="filter-senders">
        <div className="flex items-start gap-2 mb-3">
          <Prohibit size={18} className="text-[#8a0707] mt-0.5 flex-shrink-0" weight="duotone" />
          <div>
            <h4 className="text-sm font-semibold text-[#2d3338]">Skip these senders</h4>
            <p className="text-xs text-[#596065] mt-0.5">
              Add emails, domains, or wildcards. Examples: <code className="text-[#004faa]">mom@gmail.com</code>,{' '}
              <code className="text-[#004faa]">@familydomain.com</code>,{' '}
              <code className="text-[#004faa]">*@apple.com</code>
            </p>
          </div>
        </div>
        <ChipInput
          value={prefs.sender_blocklist || []}
          onChange={(next) => setPrefs((p) => ({ ...p, sender_blocklist: next }))}
          placeholder="Type an email or domain and press Enter"
          testIdPrefix="sender"
          validate={validateSender}
        />
      </section>

      {/* C — Category policy (swapped next to A so fast filters are together) */}
      <section className="p-5 border-b border-[#ebeef2]" data-testid="filter-categories">
        <div className="flex items-start gap-2 mb-3">
          <Tag size={18} className="text-[#0f6b3a] mt-0.5 flex-shrink-0" weight="duotone" />
          <div>
            <h4 className="text-sm font-semibold text-[#2d3338]">Gmail categories to scan</h4>
            <p className="text-xs text-[#596065] mt-0.5">
              Gmail sorts your inbox into categories. Keep only the ones LifeAdmin should read.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const checked = !!prefs.category_policy?.[key];
            return (
              <label
                key={key}
                className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-all ${
                  checked ? 'bg-[#f0fdf4] border-[#bbf7d0]' : 'bg-white border-[#ebeef2] hover:border-[#c7d2fe]'
                }`}
                data-testid={`cat-toggle-${key}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCategory(key)}
                  className="accent-[#0f6b3a] flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2d3338] leading-tight">{meta.label}</p>
                  <p className="text-[11px] text-[#596065] leading-tight truncate">{meta.help}</p>
                </div>
              </label>
            );
          })}
        </div>
        {enabledCategoriesCount === 0 && (
          <p className="text-xs text-[#a61919] mt-2">
            ⚠ All categories are off — LifeAdmin won't find anything to scan.
          </p>
        )}
      </section>

      {/* B — Label blocklist — user types Gmail labels they already use
          (Personal, Family, Friends, Work, etc). We skip the slow Gmail API
          fetch so the panel is instant, and the scan query uses `-label:<name>`
          so any label name works as-is. */}
      <section className="p-5 border-b border-[#ebeef2] lg:col-span-2" data-testid="filter-labels">
        <div className="flex items-start gap-2 mb-3">
          <Tag size={18} className="text-[#5b21b6] mt-0.5 flex-shrink-0" weight="duotone" />
          <div>
            <h4 className="text-sm font-semibold text-[#2d3338]">Skip Gmail labels</h4>
            <p className="text-xs text-[#596065] mt-0.5">
              Type labels you already use in Gmail — e.g. <code className="text-[#004faa]">Personal</code>,{' '}
              <code className="text-[#004faa]">Family</code>, <code className="text-[#004faa]">Friends</code> —
              and we'll never scan emails under them. Case-insensitive.
            </p>
          </div>
        </div>
        <ChipInput
          value={prefs.label_blocklist || []}
          onChange={(next) => setPrefs((p) => ({ ...p, label_blocklist: next }))}
          placeholder="Type a Gmail label and press Enter"
          testIdPrefix="label"
        />
      </section>

      </div>{/* end grid */}

      {/* Footer actions */}
      <div className="p-5 bg-[#f8fafc] flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[11px] text-[#596065] max-w-md">
          Changes apply to future scans. Existing tasks aren't removed — a retroactive
          cleanup toggle is coming soon.
        </p>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="flex items-center gap-1 text-xs text-[#0f6b3a] font-medium" data-testid="filters-saved">
              <CheckCircle size={14} weight="fill" /> Saved
            </span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-[#005bc1] text-white text-sm font-semibold hover:bg-[#004faa] transition-all flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="save-filters-btn"
          >
            {saving ? <SpinnerGap className="animate-spin" size={14} /> : <FloppyDisk size={14} weight="bold" />}
            {saving ? 'Saving…' : 'Save filters'}
          </button>
        </div>
      </div>
    </div>
  );
}
